export const MATERIAL_CENTER_MODULE_CODE = 'material-center'
export const MATERIAL_CENTER_MENU_CODE = 'material-center.library'

// 内容审核：词库 / 策略 / 日志（module + 三个菜单 code 由后端注册，前端按 moduleCode 分发）
export const CONTENT_REVIEW_MODULE_CODE = 'content-review'
export const CONTENT_REVIEW_LEXICON_MENU_CODE = 'content-review.lexicon'
export const CONTENT_REVIEW_POLICY_MENU_CODE = 'content-review.policy'
export const CONTENT_REVIEW_LOGS_MENU_CODE = 'content-review.logs'

const TENANT_DECORATION_INFRASTRUCTURE_MODULE_CODES = new Set([
  'decoration',
  'material-center',
  'open-platform',
  'push',
  'qrcode'
])

const remoteNameAliases: Record<string, string> = {
  'comment-center': 'mfe_comment_saas',
  guoxue: 'mfe_guoxue_saas',
  qrcode: 'mfe_qrcode_saas',
  'qrcode-ops': 'mfe_qrcode_saas',
  ticketing: 'mfe_ticketing_saas',
  cinema: 'mfe_cinema_saas',
  'movie-product': 'mfe_movie_product_saas',
  'ticketing-channel': 'mfe_ticketing_saas',
  'market-compute': 'mfe_market_compute_saas',
  'market-ota': 'mfe_market_ota_saas',
  'market-selection': 'mfe_market_selection_saas',
  'market-supplychain': 'mfe_market_supplychain_saas',
  pcppay: 'mfe_payment_saas'
}

const menuNamespaceAliases: Record<string, string> = {
  'comment-center': 'comment'
}

// 市场中心分组（docs/architecture/market-center-landscape.md）：四个 market-* 产品 + 票务渠道中心（抖音/美团分发，
// 本质是"反向市场"，表仍归票务）。装修模板市场（decoration.market）按 Phase 2 spec 保持独立，不在此列。
const MARKET_CENTER_MODULE_CODES = new Set([
  'market-supplychain',
  'market-selection',
  'market-compute',
  'market-ota',
  'ticketing-channel'
])

export type ShellMenuKind = 'remote' | 'module-user' | 'decoration' | 'material' | 'content-review'

interface MergeableMenu {
  code: string
  remoteEntry?: string
  exposedModule?: string
  permission?: string
}

interface ModuleRootMenu {
  code: string
  name: string
  parentCode?: string | null
  hasChildren?: boolean
}

interface AuthorizedRemoteMenuClaims {
  tenantId: number | null
  moduleCode: string
  menuCode: string
  permission: string
  entitledModules: readonly string[]
  effectiveMenuCodes?: readonly string[] | null
  grantedPermissions: readonly string[]
}

interface ModuleWorkbenchMenu {
  moduleCode: string
  code: string
  name: string
  hasChildren?: boolean
}

export interface ModuleWorkbenchNavigation<T extends ModuleWorkbenchMenu> {
  menu: T | null
  error: string
}

export function isAuthorizedRemoteMenu(claims: AuthorizedRemoteMenuClaims) {
  if (!claims.tenantId || claims.tenantId <= 0) return false
  if (!claims.entitledModules.includes(claims.moduleCode)) return false
  if (claims.effectiveMenuCodes != null && !claims.effectiveMenuCodes.includes(claims.menuCode)) return false
  if (claims.grantedPermissions.includes('*')) return true
  // 菜单 permission_code 可为逗号分隔的 any-of 组合（与后端 @RequiresApiPermission 语义一致）：
  // 任一权限命中即可见菜单，例如提现审核菜单 withdraw:audit,withdraw:payout（V335）。
  const requiredPermissions = claims.permission
    .split(',')
    .map((permission) => permission.trim())
    .filter(Boolean)
  return requiredPermissions.some((permission) => claims.grantedPermissions.includes(permission))
}

export function isModuleNavigationRoot(menu: ModuleRootMenu) {
  return Boolean(menu.hasChildren && !menu.parentCode)
}

export function isMarketCenterModule(moduleCode: string) {
  return MARKET_CENTER_MODULE_CODES.has(moduleCode)
}

export function resolveModuleMenuNamespace(moduleCode: string) {
  return menuNamespaceAliases[moduleCode] ?? moduleCode
}

export function resolveModuleWorkbenchNavigation<T extends ModuleWorkbenchMenu>(
  menus: readonly T[],
  moduleCode: string
): ModuleWorkbenchNavigation<T> {
  const menuNamespace = resolveModuleMenuNamespace(moduleCode)
  const openableMenus = menus.filter((menu) =>
    menu.moduleCode === moduleCode
    && (!menu.hasChildren || (moduleCode === 'comment-center' && menu.code === 'comment.workbench'))
  )
  for (const code of [`${menuNamespace}.workbench`, `${menuNamespace}.dashboard`]) {
    const menu = openableMenus.find((item) => item.code === code)
    if (menu) return { menu, error: '' }
  }

  const semanticMenu = openableMenus.find((menu) =>
    /工作台|经营总览|运营总览/.test(menu.name.trim())
  )
  if (semanticMenu) return { menu: semanticMenu, error: '' }

  return {
    menu: null,
    error: `模块 ${moduleCode} 没有可访问的工作台。`
  }
}

export function isLegacyMaterialMenu(moduleCode: string, menuCode: string) {
  return moduleCode !== MATERIAL_CENTER_MODULE_CODE && menuCode.endsWith('.materials')
}

export function isLegacyProductMenu(menuCode: string) {
  return menuCode === 'ticketing.market'
    || menuCode === 'ticketing.listings'
    || menuCode.startsWith('selection-market.')
    || menuCode.startsWith('distribution.')
}

export function resolveShellMenuKind(moduleCode: string, menuCode: string): ShellMenuKind {
  if (moduleCode === MATERIAL_CENTER_MODULE_CODE && menuCode === MATERIAL_CENTER_MENU_CODE) return 'material'
  if (menuCode.endsWith('.module-users')) return 'module-user'
  // 票务店铺装修走 mfe 店铺维度（StoreDecoration 按 shop-{shopId}-home 页面加载/保存），
  // 不走通用装修管理器（其默认裸 home 无店铺维度）
  if (moduleCode === 'ticketing'
    && (menuCode === 'ticketing.decoration' || menuCode === 'ticketing.decoration.pages')) {
    return 'remote'
  }
  if (isDecorationWorkspaceMenu(moduleCode, menuCode)) return 'decoration'
  if (moduleCode === CONTENT_REVIEW_MODULE_CODE) return 'content-review'
  return 'remote'
}

export function isDecorationWorkspaceMenu(moduleCode: string, menuCode: string) {
  // 多站点模块（CMS / Content）装修必须走模块内 MFE 路由，不走通用 Shell 装修管理器（需要 siteId）
  if (moduleCode === 'cms' || moduleCode === 'content') return false
  if (moduleCode === 'decoration') return true
  return menuCode === `${moduleCode}.decoration`
    || menuCode === `${moduleCode}.decoration.pages`
    || menuCode === `${moduleCode}.decoration.market`
}

export function decorationPagesMenuCode(moduleCode: string) {
  return moduleCode === 'decoration' ? 'decoration.pages' : `${moduleCode}.decoration.pages`
}

export function decorationHomeMenuCode(moduleCode: string) {
  return moduleCode === 'decoration' ? 'decoration.home' : `${moduleCode}.decoration`
}

export function decorationMarketMenuCode(moduleCode: string) {
  return moduleCode === 'decoration' ? 'decoration.market' : `${moduleCode}.decoration.market`
}

/**
 * 模块作用域装修默认页码解析（G4 契约修复）：模块装修目录页码是模块自有约定
 * （如 OPC 白名单 opc-home/opc-*-header），通用约定页码 home/app 不在模块白名单内，
 * 直接按 home 请求会被模块装饰控制器以 DECORATION_PAGE_CODE_INVALID 拒绝。因此
 * 模块作用域入口必须先取模块装修页列表（GET /api/saas/{module}/decoration/pages），
 * 再按 <module>-home 优先、目录首项兜底解析合法默认页码；目录为空返回 ''（入口提示
 * 无可装修页面）。租户级装修（moduleCode='decoration'）仍走 home 通用约定。
 */
export function resolveDecorationDefaultPageCode(moduleCode: string, pageCodes: ReadonlyArray<string>) {
  const scoped = moduleCode && moduleCode !== 'decoration'
  const moduleHome = scoped ? `${moduleCode}-home` : 'home'
  if (pageCodes.includes(moduleHome)) return moduleHome
  if (!scoped) return pageCodes.includes('home') ? 'home' : pageCodes[0] ?? ''
  return pageCodes[0] ?? ''
}

/** 装修页列表接口返回分页对象 `{ records }`，调用方一律先解开成数组。 */
export function decorationPageList<T extends { pageCode: string }>(
  result: ReadonlyArray<T> | {
    records?: ReadonlyArray<T> | null
    total?: number
    pageNo?: number
    pageSize?: number
  } | null | undefined
): T[] {
  if (Array.isArray(result)) return Array.from(result as ReadonlyArray<T>)
  const records = (result as { records?: ReadonlyArray<T> | null } | null | undefined)?.records
  if (Array.isArray(records)) return Array.from(records)
  return []
}

/** 从模块装修页列表取页码清单（去重，保持后端排序）。 */
export function decorationPageCodes(pages: ReadonlyArray<{ pageCode: string }>) {
  const seen = new Set<string>()
  const codes: string[] = []
  for (const page of pages) {
    const code = String(page?.pageCode || '').trim().toLowerCase()
    if (code && !seen.has(code)) {
      seen.add(code)
      codes.push(code)
    }
  }
  return codes
}

export function resolveTenantDecorationModuleCode(
  tenantDomain: string,
  modules: ReadonlyArray<{ moduleCode: string }>
) {
  const moduleCodes = modules
    .map((module) => module.moduleCode.trim().toLowerCase())
    .filter(Boolean)
  const domainModuleCode = tenantDomain.trim().toLowerCase().split('.')[0] || ''
  if (domainModuleCode && moduleCodes.includes(domainModuleCode)) return domainModuleCode

  const businessModules = moduleCodes.filter(
    (moduleCode) => !TENANT_DECORATION_INFRASTRUCTURE_MODULE_CODES.has(moduleCode)
  )
  return businessModules.length === 1 ? businessModules[0] : ''
}

export function canonicalWorkspaceMenuCode(menuCode: string) {
  if (menuCode === 'ticketing.cinemaAdmin') return 'cinema.workbench'
  if (menuCode === 'ticketing.seatMap') return 'cinema.seats'
  if (menuCode === 'ticketing.cinemaProvider') return 'movie-product.providers'
  if (menuCode === 'decoration.app-shell') return 'decoration.pages'
  if (menuCode === 'selection-market.offers') return 'selection-market.home'
  return menuCode.endsWith('.materials') ? MATERIAL_CENTER_MENU_CODE : menuCode
}

export function resolveSaasRemoteName(moduleCode: string) {
  return remoteNameAliases[moduleCode] ?? `mfe_${moduleCode}_saas`
}

export function legacyMaterialScope(menuCode: string) {
  return menuCode.endsWith('.materials') ? menuCode.slice(0, -'.materials'.length) : ''
}

export function mergeMenuDefinitions<T extends MergeableMenu>(configured: T[], catalog: T[]): T[] {
  // 后端数据可能重复（同一 code 多份拷贝），合并前按 code 去重，避免侧边栏菜单重复渲染
  const uniqueByCode = (menus: T[]): T[] => {
    const seen = new Set<string>()
    return menus.filter((menu) => {
      if (seen.has(menu.code)) return false
      seen.add(menu.code)
      return true
    })
  }
  configured = uniqueByCode(configured)
  catalog = uniqueByCode(catalog)
  const catalogByCode = new Map(catalog.map((menu) => [menu.code, menu]))
  const merged = configured.map((menu) => {
    const fallback = catalogByCode.get(menu.code)
    if (!fallback) return menu
    return {
      ...fallback,
      ...menu,
      remoteEntry: menu.remoteEntry || fallback.remoteEntry,
      exposedModule: menu.exposedModule || fallback.exposedModule,
      // 菜单权限码以模块目录为权威源：租户 sys_menu 旧行（如 audit-only）不得覆盖目录
      // any-of 组合码，否则 payout-only 运营在 visibleMenus 过滤中不可见（V335）。
      // 目录未提供权限码时保留租户现值（不引入 undefined 键）。
      ...(fallback.permission ? { permission: fallback.permission } : {})
    }
  })
  const configuredCodes = new Set(configured.map((menu) => menu.code))
  merged.push(...catalog.filter((menu) => !configuredCodes.has(menu.code)))
  return merged
}
